import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import Logo from "../components/Logo";
import api from "../api";
import toast from "react-hot-toast";
import CompanyVerifyModal from "../components/CompanyVerifyModal";
import {
  ArrowRight, UserSearch, Mail, Globe, User, MapPin, Search,
  Hourglass, CheckCircle2, XCircle, Scale, Coins, Gift,
  AlertTriangle, Info, Radar, Layers, FileCode2, FileText, Download, Lock,
} from "lucide-react";

const DEFAULT_TOOL_COSTS = { holehe: 3, theHarvester: 5, WhatWeb: 2, Photon: 5 };

/* ─────────── OSINT result beautifiers ─────────── */
const EMAIL_RE = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]*?\.(?:com|in|io|org|net|co|app|dev|gov|edu|ai)\b/gi;
const uniq = (a) => [...new Set(a)];
const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
const prettyTool = (t) => {
  const k = String(t).toLowerCase();
  if (k.startsWith("holehe")) {
    const svc = k.split("_")[1];
    return svc ? `Email Footprint · ${cap(svc)}` : "Email Footprint";
  }
  if (k.includes("harvester")) return "Domain & Email Recon";
  if (k.includes("whatweb")) return "Tech Fingerprint";
  if (k.includes("photon")) return "Site Intelligence";
  return String(t).replace(/_/g, " ");
};
const toolIcon = (t) => {
  const k = String(t).toLowerCase();
  if (k.startsWith("holehe")) return Mail;
  if (k.includes("harvester")) return Search;
  if (k.includes("whatweb")) return Layers;
  if (k.includes("photon")) return Radar;
  return Search;
};

function Chips({ items, tone = "default", icon: Icon }) {
  if (!items || !items.length) return null;
  const good = tone === "good";
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
      {items.map((it, i) => (
        <span key={i} style={{
          fontSize: 12, padding: "5px 12px", borderRadius: "var(--au-r-pill)",
          display: "inline-flex", alignItems: "center", gap: 6, wordBreak: "break-all",
          background: good ? "rgba(31,208,163,0.12)" : "var(--au-glass)",
          border: `1px solid ${good ? "var(--au-accent)" : "var(--au-border)"}`,
          color: good ? "var(--au-accent-ink)" : "var(--au-text-2)",
        }}>{Icon && <Icon size={12} />}{it}</span>
      ))}
    </div>
  );
}
function EmptyNote({ icon: Icon = CheckCircle2, text, ok = true }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, color: "var(--au-muted)", fontSize: 13, padding: "8px 0" }}>
      <Icon size={16} style={{ color: ok ? "var(--au-success)" : "var(--au-muted)" }} /> {text}
    </div>
  );
}
function ErrNote({ text }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, color: "var(--au-warn)", fontSize: 13, background: "rgba(245,180,60,0.08)", border: "1px solid rgba(245,180,60,0.28)", borderRadius: "var(--au-r-input)", padding: "10px 14px" }}>
      <AlertTriangle size={16} style={{ flex: "none" }} /> {text}
    </div>
  );
}
function SubHead({ label, count }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "16px 0 10px", fontSize: 11, letterSpacing: 1.4, textTransform: "uppercase", color: "var(--au-muted)", fontWeight: 700 }}>
      {label}{count != null && <span style={{ background: "var(--au-glass-strong)", color: "var(--au-text-2)", borderRadius: 20, padding: "1px 9px", fontSize: 11, fontWeight: 700 }}>{count}</span>}
    </div>
  );
}
function LinkList({ items, max = 10 }) {
  const [open, setOpen] = useState(false);
  if (!items || !items.length) return null;
  const shown = open ? items : items.slice(0, max);
  return (
    <div>
      <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 4 }}>
        {shown.map((it, i) => (
          <li key={i} className="au-mono" style={{ fontSize: 12, color: "var(--au-text-2)", padding: "6px 10px", background: "var(--au-glass)", border: "1px solid var(--au-border)", borderRadius: 8, wordBreak: "break-all" }}>{String(it)}</li>
        ))}
      </ul>
      {items.length > max && (
        <button onClick={() => setOpen(!open)} className="au-pill au-pill-glass au-pill-sm" style={{ marginTop: 10 }}>
          {open ? "Show less" : `Show all ${items.length}`}
        </button>
      )}
    </div>
  );
}
function StatRow({ stats }) {
  const shown = stats.filter(([, n]) => n > 0);
  if (!shown.length) return null;
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
      {shown.map(([l, n]) => (
        <div key={l} style={{ flex: "1 1 84px", minWidth: 84, textAlign: "center", background: "var(--au-glass)", border: "1px solid var(--au-border)", borderRadius: "var(--au-r-input)", padding: "12px 8px" }}>
          <div style={{ fontSize: 22, fontWeight: 800, color: "var(--au-accent-ink)", lineHeight: 1 }}>{n}</div>
          <div style={{ fontSize: 10, letterSpacing: 1, textTransform: "uppercase", color: "var(--au-muted)", marginTop: 4 }}>{l}</div>
        </div>
      ))}
    </div>
  );
}

function PhotonResult({ output }) {
  const data = (output && output["exported.json"]) || output || {};
  const arr = (k) => (Array.isArray(data[k]) ? data[k] : []);
  const intel = arr("intel");
  const emails = uniq(intel.flatMap((s) => String(s).match(EMAIL_RE) || []).map((e) => e.toLowerCase()));
  const robots = arr("robots"), internal = arr("internal"), external = arr("external");
  const scripts = arr("scripts"), endpoints = arr("endpoints"), keys = arr("keys");
  const fuzzable = arr("fuzzable"), files = arr("files");
  const nothing = !emails.length && !robots.length && !internal.length && !scripts.length && !external.length && !endpoints.length;
  return (
    <div>
      <StatRow stats={[["Emails", emails.length], ["Internal", internal.length], ["External", external.length], ["Scripts", scripts.length], ["Robots", robots.length], ["Endpoints", endpoints.length], ["Keys", keys.length], ["Files", files.length]]} />
      {emails.length > 0 && (<><SubHead label="Emails found" count={emails.length} /><Chips items={emails} tone="good" icon={Mail} /></>)}
      {keys.length > 0 && (<><SubHead label="Secrets / API keys" count={keys.length} /><Chips items={keys} /></>)}
      {robots.length > 0 && (<><SubHead label="robots.txt paths" count={robots.length} /><LinkList items={robots} max={8} /></>)}
      {endpoints.length > 0 && (<><SubHead label="Endpoints" count={endpoints.length} /><LinkList items={endpoints} max={8} /></>)}
      {scripts.length > 0 && (<><SubHead label="Scripts" count={scripts.length} /><LinkList items={scripts} max={6} /></>)}
      {external.length > 0 && (<><SubHead label="External links" count={external.length} /><LinkList items={external} max={8} /></>)}
      {fuzzable.length > 0 && (<><SubHead label="Fuzzable URLs" count={fuzzable.length} /><LinkList items={fuzzable} max={8} /></>)}
      {internal.length > 0 && (<><SubHead label="Internal links" count={internal.length} /><LinkList items={internal} max={10} /></>)}
      {nothing && <EmptyNote icon={Info} ok={false} text="No crawl intelligence extracted from the target." />}
    </div>
  );
}
function HoleheResult({ output }) {
  const a = Array.isArray(output) ? output : [];
  const found = a.filter((x) => x && x.exists === true).map((x) => x.name || x.domain).filter(Boolean);
  if (!a.length) return <EmptyNote text="No registered accounts found for this email." />;
  if (!found.length) return <EmptyNote text={`Checked ${a.length} services — no accounts found.`} />;
  return (<><SubHead label="Accounts found" count={found.length} /><Chips items={found} tone="good" icon={CheckCircle2} /></>);
}
function HarvesterResult({ output }) {
  if (output && output.error) return <ErrNote text={output.error} />;
  const emails = (output && output.emails) || [];
  const hosts = (output && output.hosts) || [];
  if (!emails.length && !hosts.length) return <EmptyNote icon={Info} ok={false} text="No emails or hosts discovered." />;
  return (<>
    {emails.length > 0 && (<><SubHead label="Emails" count={emails.length} /><Chips items={emails} tone="good" icon={Mail} /></>)}
    {hosts.length > 0 && (<><SubHead label="Hosts" count={hosts.length} /><LinkList items={hosts} /></>)}
  </>);
}
function WhatWebResult({ output }) {
  const a = Array.isArray(output) ? output : [output];
  const err = a.find((x) => x && x.error);
  if (err) return <ErrNote text={err.error} />;
  const first = a[0] || {};
  const plugins = first.plugins ? Object.keys(first.plugins) : [];
  if (!plugins.length) return <EmptyNote icon={Info} ok={false} text="No technology fingerprints returned." />;
  return (<>
    {first.target && <SubHead label={first.target} />}
    <Chips items={plugins} icon={Layers} />
  </>);
}
function ToolResult({ name, output }) {
  const k = String(name).toLowerCase();
  if (k.startsWith("holehe")) return <HoleheResult output={output} />;
  if (k.includes("harvester")) return <HarvesterResult output={output} />;
  if (k.includes("whatweb")) return <WhatWebResult output={output} />;
  if (k.includes("photon")) return <PhotonResult output={output} />;
  return (
    <pre className="au-mono" style={{ fontSize: 12, color: "var(--au-text-2)", lineHeight: 1.7, whiteSpace: "pre-wrap", wordBreak: "break-all", background: "var(--au-glass)", border: "1px solid var(--au-border)", borderRadius: "var(--au-r-input)", padding: 18, margin: 0, maxHeight: 500, overflowY: "auto" }}>
      {JSON.stringify(output, null, 2)}
    </pre>
  );
}

const OsintScanner = () => {
  const [target, setTarget] = useState("");
  const [loading, setLoading] = useState(false);
  const [scanId, setScanId] = useState(null);
  const [status, setStatus] = useState(null);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const pollCount = useRef(0);
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [disclaimerText, setDisclaimerText] = useState("");
  const [showCompanyVerify, setShowCompanyVerify] = useState(false);
  const [toolCosts, setToolCosts] = useState(DEFAULT_TOOL_COSTS);
  const [creditBalance, setCreditBalance] = useState(0);
  const [osintFreeRemaining, setOsintFreeRemaining] = useState(0);
  const [osintDownloads, setOsintDownloads] = useState([]);
  const nav = useNavigate();

  const [selectedTools, setSelectedTools] = useState({
    holehe: true,
    theHarvester: true,
    WhatWeb: true,
    Photon: true
  });

  const scanCost = Object.keys(selectedTools)
    .reduce((sum, t) => sum + (selectedTools[t] ? (toolCosts[t] || 0) : 0), 0);

  const refreshBalance = () =>
    api.get("/api/osint/pricing").then(r => {
      setCreditBalance(r.data.credits || 0);
      setOsintFreeRemaining(r.data.osint_free_remaining || 0);
    }).catch(() => {});

  useEffect(() => {
    api.get("/api/scan/disclaimer").then(r => setDisclaimerText(r.data.disclaimer)).catch(() => {});
    api.get("/api/osint/pricing").then(r => {
      if (r.data.tool_costs) setToolCosts(r.data.tool_costs);
      setCreditBalance(r.data.credits || 0);
      setOsintFreeRemaining(r.data.osint_free_remaining || 0);
      setOsintDownloads(Array.isArray(r.data.osint_downloads) ? r.data.osint_downloads : []);
    }).catch(() => {});
  }, []);

  const downloadReport = async (fmt) => {
    if (!scanId) return;
    try {
      const res = await api.get(`/api/osint/report/${scanId}?format=${fmt}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `osint_${results?.target || target || scanId}.${fmt}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      let msg = err.response?.data?.detail || `Failed to download ${fmt.toUpperCase()} report`;
      // blob error responses arrive as a Blob — try to read the JSON detail out of it
      if (err.response?.data instanceof Blob) {
        try {
          const txt = await err.response.data.text();
          msg = JSON.parse(txt)?.detail || msg;
        } catch { /* keep fallback msg */ }
      }
      toast.error(msg);
    }
  };

  // Form submit -> require legal consent before running anything
  const requestScan = (e) => {
    e.preventDefault();
    if (!target) return;
    if (!Object.values(selectedTools).some(v => v)) return;
    setAgreed(false);
    setShowDisclaimer(true);
  };

  const startScan = async () => {
    if (!agreed) { toast.error("Please agree to the disclaimer"); return; }
    setShowDisclaimer(false);

    setLoading(true);
    setScanId(null);
    setStatus(null);
    setResults(null);
    setError(null);
    pollCount.current = 0;

    try {
      const activeTools = Object.keys(selectedTools).filter(t => selectedTools[t]);
      const res = await api.post("/api/osint/scan", { target, selected_tools: activeTools, consent: true });
      if (res.data.success) {
        setScanId(res.data.scan_id);
        setStatus("queued");
        if (typeof res.data.credits_remaining === "number") setCreditBalance(res.data.credits_remaining);
        if (typeof res.data.osint_free_remaining === "number") setOsintFreeRemaining(res.data.osint_free_remaining);
        toast.success(res.data.was_free
          ? "OSINT scan started · used 1 free monthly scan"
          : res.data.credits_used > 0
            ? `OSINT scan started · ${res.data.credits_used} credits used`
            : "OSINT scan started");
      }
    } catch (err) {
      console.error(err);
      const st = err.response?.status;
      const msg = err.response?.data?.detail || "Failed to start scan";
      setError(msg);
      setLoading(false);
      if (st === 403 && msg.includes("COMPANY_VERIFY_REQUIRED")) {
        setShowCompanyVerify(true);
      } else if (st === 402) {
        toast.error(msg + " Redirecting to wallet...");
        setTimeout(() => nav("/wallet"), 2000);
      } else {
        toast.error(msg);
      }
    }
  };

  useEffect(() => {
    let interval;
    if (scanId && (status === "queued" || status === "running")) {
      interval = setInterval(async () => {
        // Give up after ~5 minutes (100 * 3s) so a stuck scan doesn't spin forever
        pollCount.current += 1;
        if (pollCount.current > 100) {
          clearInterval(interval);
          setStatus("failed");
          setError("Scan timed out. Please try again.");
          setLoading(false);
          toast.error("Scan timed out");
          refreshBalance();
          return;
        }
        try {
          const res = await api.get(`/api/osint/status/${scanId}`);
          setStatus(res.data.status);

          if (res.data.status === "done") {
            const resultRes = await api.get(`/api/osint/results/${scanId}`);
            setResults(resultRes.data);
            setLoading(false);
            clearInterval(interval);
            toast.success("Scan completed!");
          } else if (res.data.status === "failed") {
            setError(res.data.error || "Scan failed");
            setLoading(false);
            clearInterval(interval);
            toast.error("Scan failed — credits refunded");
            refreshBalance();
          }
        } catch (err) {
          console.error("Poll error", err);
        }
      }, 3000);
    }
    return () => clearInterval(interval);
  }, [scanId, status]);

  const TypeIcon = (type) => {
    switch (type) {
      case "email": return Mail;
      case "domain": return Globe;
      case "username": return User;
      case "ip": return MapPin;
      default: return Search;
    }
  };

  const ResultTypeIcon = results ? TypeIcon(results.type) : Search;
  const StatusIcon = (status === "queued" || status === "running") ? Hourglass : status === "done" ? CheckCircle2 : XCircle;
  const statusColor = (status === "queued" || status === "running") ? "var(--au-warn)" : status === "done" ? "var(--au-success)" : "var(--au-error)";

  return (
    <div className="au-root">
      <nav style={S.nav}>
        <Link to="/" style={{textDecoration:"none"}}><Logo size="small" /></Link>
        <div style={{display:"flex",alignItems:"center",gap:18}}>
          <Link to="/dashboard" style={{color:"var(--au-text-2)",textDecoration:"none",fontSize:13,display:"inline-flex",alignItems:"center",gap:6}}><ArrowRight size={14} style={{transform:"rotate(180deg)"}}/> Dashboard</Link>
          <button onClick={() => { localStorage.clear(); sessionStorage.clear(); window.location.replace("/"); }} className="au-pill au-pill-glass au-pill-sm">Logout</button>
        </div>
      </nav>

      <div style={{maxWidth:960,margin:"0 auto",padding:"40px 24px"}}>
        <div style={{textAlign:"center",marginBottom:40}}>
          <h1 style={{fontSize:30,fontWeight:700,color:"var(--au-text)",letterSpacing:2,marginBottom:6,display:"inline-flex",alignItems:"center",gap:12}}><UserSearch size={28} style={{color:"var(--au-accent-ink)"}}/> OSINT Target Analyzer</h1>
          <p style={{color:"var(--au-muted)",marginBottom:18}}>Intelligently gather open-source intelligence on domains, emails, usernames, or IPs</p>
        </div>

        <form onSubmit={requestScan} style={{display:"flex",flexDirection:"column",gap:16,maxWidth:700,margin:"0 auto 40px auto"}}>
          <div style={{display:"flex",gap:12}}>
            <input
              type="text"
              className="au-input au-mono"
              style={{flex:1}}
              placeholder="Enter domain, email, username, or IP address..."
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              disabled={loading}
            />
            <button type="submit" disabled={loading || !target || !Object.values(selectedTools).some(v=>v)} className="au-pill au-pill-primary">
              {loading ? "Scanning…" : <>Analyze <ArrowRight size={16}/></>}
            </button>
          </div>

          <div style={{display:"flex",justifyContent:"center",alignItems:"center",gap:12,flexWrap:"wrap",fontSize:13}}>
            {osintFreeRemaining > 0 ? (
              <span style={{color:"var(--au-success)",display:"inline-flex",alignItems:"center",gap:6}}>
                <Gift size={15} style={{color:"var(--au-success)"}}/>
                {osintFreeRemaining} free OSINT scan{osintFreeRemaining === 1 ? "" : "s"} left this month — this scan is <strong style={{color:"var(--au-success)"}}>FREE</strong>
              </span>
            ) : (
              <span style={{color:"var(--au-text-2)",display:"inline-flex",alignItems:"center",gap:6}}>
                <Coins size={15} style={{color:"var(--au-accent-ink)"}}/>
                This scan uses <strong style={{color:"var(--au-accent-ink)"}}>{scanCost}</strong> credit{scanCost === 1 ? "" : "s"}
              </span>
            )}
            <span style={{color:"var(--au-muted)"}}>·</span>
            <span style={{color: (osintFreeRemaining === 0 && creditBalance < scanCost) ? "var(--au-error)" : "var(--au-muted)"}}>
              Balance: <strong>{creditBalance}</strong>
            </span>
            {osintFreeRemaining === 0 && creditBalance < scanCost && (
              <Link to="/wallet" className="au-pill au-pill-glass au-pill-sm">Add credits</Link>
            )}
          </div>
        </form>

        {status && (
          <div className="au-glass au-pad" style={{marginBottom:20}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:14,marginBottom:8}}>
              <StatusIcon size={26} style={{color:statusColor}}/>
              <h3 style={{fontSize:22,fontWeight:700,color:"var(--au-text)",textTransform:"capitalize"}}>{status}</h3>
            </div>
            <p style={{color:"var(--au-muted)",fontSize:14,textAlign:"center"}}>
              {status === "queued" && "Waiting in queue..."}
              {status === "running" && "Gathering intelligence... This may take a few minutes."}
              {status === "done" && "Intelligence gathering complete."}
              {status === "failed" && (error || "An error occurred during the scan.")}
            </p>
            {(status === "queued" || status === "running") && (
              <div style={{marginTop:14}}>
                <div style={{background:"var(--au-glass-strong)",borderRadius:3,height:4,overflow:"hidden"}}>
                  <div style={{height:"100%",width:"60%",background:"var(--au-accent)",borderRadius:3}}/>
                </div>
              </div>
            )}
          </div>
        )}

        {results && (
          <>
            <div className="au-glass au-pad" style={{marginBottom:20,display:"flex",alignItems:"center",gap:16,flexWrap:"wrap"}}>
              <span className="au-badge"><ResultTypeIcon size={24}/></span>
              <div style={{flex:1,minWidth:160}}>
                <h2 className="au-mono" style={{fontSize:20,fontWeight:700,color:"var(--au-text)"}}>{results.target}</h2>
                <p style={{color:"var(--au-muted)",fontSize:11,letterSpacing:2,textTransform:"uppercase",marginTop:4}}>Target Type: {results.type}</p>
              </div>
              <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                <span style={{display:"inline-flex",alignItems:"center",gap:6,fontSize:11,letterSpacing:1.2,textTransform:"uppercase",color:"var(--au-muted)",fontWeight:700}}>
                  <Download size={14}/> Download report
                </span>
                {[["pdf", FileText], ["html", FileCode2]].map(([fmt, Icon]) => {
                  const enabled = osintDownloads.includes(fmt);
                  return enabled ? (
                    <button
                      key={fmt}
                      onClick={() => downloadReport(fmt)}
                      className="au-pill au-pill-glass au-pill-sm"
                      style={{display:"inline-flex",alignItems:"center",gap:6}}
                    >
                      <Icon size={14}/> {fmt.toUpperCase()}
                    </button>
                  ) : (
                    <span
                      key={fmt}
                      title="Upgrade to download"
                      className="au-pill au-pill-glass au-pill-sm"
                      style={{display:"inline-flex",alignItems:"center",gap:6,opacity:0.45,cursor:"not-allowed"}}
                    >
                      <Lock size={14}/> {fmt.toUpperCase()}
                    </span>
                  );
                })}
              </div>
            </div>

            {results.correlation && results.correlation.summary && (
              <div className="au-glass au-pad" style={{marginBottom:20}}>
                <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:14,paddingBottom:12,borderBottom:"1px solid var(--au-hair)"}}>
                  <span className="au-badge" style={{width:34,height:34}}><Layers size={17}/></span>
                  <span style={{fontSize:14,fontWeight:700,color:"var(--au-text)"}}>Correlated Intelligence</span>
                  <span style={{fontSize:11,color:"var(--au-muted)"}}>merged across all sources</span>
                </div>
                <StatRow stats={[
                  ["Emails", results.correlation.summary.emails],
                  ["Hosts", results.correlation.summary.hosts],
                  ["Tech", results.correlation.summary.technologies],
                  ["Accounts", results.correlation.summary.accounts],
                  ["Endpoints", results.correlation.summary.endpoints],
                  ["Corroborated", results.correlation.summary.corroborated_entities],
                ]}/>
                {(results.correlation.signals || []).length > 0 && (
                  <>
                    <SubHead label="Exposure signals" count={results.correlation.signals.length}/>
                    <div style={{display:"flex",flexDirection:"column",gap:8}}>
                      {results.correlation.signals.map((s, i) => {
                        const hi = s.severity === "high";
                        return (
                          <div key={i} style={{padding:"10px 14px",borderRadius:"var(--au-r-input)",
                            background: hi ? "rgba(244,80,80,0.08)" : "rgba(245,180,60,0.08)",
                            border:`1px solid ${hi ? "rgba(244,80,80,0.3)" : "rgba(245,180,60,0.28)"}`}}>
                            <div style={{display:"flex",alignItems:"center",gap:8,fontSize:13,fontWeight:700,color:hi?"var(--au-error)":"var(--au-warn)"}}>
                              <AlertTriangle size={14}/> {s.title}
                            </div>
                            <div style={{fontSize:12.5,color:"var(--au-text-2)",marginTop:4,lineHeight:1.6}}>{s.detail}</div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
                {(results.correlation.emails || []).filter(e => e.source_count > 1).length > 0 && (
                  <>
                    <SubHead label="Corroborated emails" count={results.correlation.emails.filter(e=>e.source_count>1).length}/>
                    <Chips items={results.correlation.emails.filter(e=>e.source_count>1).map(e=>`${e.value} (${e.source_count}×)`)} tone="good" icon={Mail}/>
                  </>
                )}
                {(results.correlation.hosts || []).length > 0 && (
                  <>
                    <SubHead label="Hosts" count={results.correlation.hosts.length}/>
                    <LinkList items={results.correlation.hosts.map(h => h.source_count > 1 ? `${h.value}  ·  ${h.source_count} sources` : h.value)} max={10}/>
                  </>
                )}
              </div>
            )}

            {Object.entries(results.tools).map(([toolName, output]) => {
              const TIcon = toolIcon(toolName);
              return (
                <div key={toolName} className="au-glass au-pad" style={{marginBottom:20}}>
                  <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:14,paddingBottom:12,borderBottom:"1px solid var(--au-hair)"}}>
                    <span className="au-badge" style={{width:34,height:34}}><TIcon size={17}/></span>
                    <span style={{fontSize:14,fontWeight:700,color:"var(--au-text)"}}>{prettyTool(toolName)}</span>
                  </div>
                  <ToolResult name={toolName} output={output} />
                </div>
              );
            })}
          </>
        )}
      </div>

      {/* DISCLAIMER MODAL */}
      {showDisclaimer && (
        <div style={S.overlay} onClick={(e) => e.target === e.currentTarget && setShowDisclaimer(false)}>
          <div className="au-glass" style={S.modal}>
            <div style={S.modalHead}>
              <Scale size={22} style={{color:"var(--au-accent-ink)",marginRight:10}}/>
              <span style={{fontSize:18,fontWeight:700,color:"var(--au-text)"}}>Legal Authorization Required</span>
            </div>
            <div style={S.disclaimerBox}>
              <pre style={{fontSize:12,color:"var(--au-text-2)",lineHeight:1.8,whiteSpace:"pre-wrap",fontFamily:"var(--au-font)",margin:0}}>{disclaimerText}</pre>
            </div>
            <div style={{display:"flex",alignItems:"flex-start",gap:10,marginBottom:20}}>
              <input type="checkbox" id="osint-agree" checked={agreed} onChange={e => setAgreed(e.target.checked)} style={{marginTop:4,cursor:"pointer",width:18,height:18,accentColor:"var(--au-accent)"}}/>
              <label htmlFor="osint-agree" style={{fontSize:13,color:"var(--au-text-2)",cursor:"pointer",lineHeight:1.6}}>
                I have read, understood, and agree to all terms above. I confirm I have authorization to gather intelligence on <strong style={{color:"var(--au-accent-ink)"}}>{target}</strong>
              </label>
            </div>
            {osintFreeRemaining > 0 ? (
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:18,fontSize:13,color:"var(--au-success)"}}>
                <Gift size={15} style={{color:"var(--au-success)"}}/>
                {osintFreeRemaining} free OSINT scan{osintFreeRemaining === 1 ? "" : "s"} left this month — this scan is <strong style={{color:"var(--au-success)"}}>FREE</strong> · balance {creditBalance}
              </div>
            ) : (
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:18,fontSize:13,color:"var(--au-text-2)"}}>
                <Coins size={15} style={{color:"var(--au-accent-ink)"}}/>
                This scan will use <strong style={{color:"var(--au-accent-ink)"}}>{scanCost}</strong> credit{scanCost === 1 ? "" : "s"} · balance {creditBalance}
              </div>
            )}
            <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
              <button onClick={() => setShowDisclaimer(false)} className="au-pill au-pill-glass au-pill-sm">Cancel</button>
              <button onClick={startScan} disabled={!agreed} className="au-pill au-pill-primary au-pill-sm" style={{opacity: agreed ? 1 : 0.4, cursor: agreed ? "pointer" : "not-allowed"}}>
                I Agree &amp; Start <ArrowRight size={14}/>
              </button>
            </div>
          </div>
        </div>
      )}

      <CompanyVerifyModal
        open={showCompanyVerify}
        onClose={() => setShowCompanyVerify(false)}
        onVerified={() => { setShowCompanyVerify(false); startScan(); }}
      />
    </div>
  );
};

export default OsintScanner;

const S = {
  nav: {padding:"14px 28px",display:"flex",alignItems:"center",justifyContent:"space-between"},
  overlay: {position:"fixed",inset:0,background:"rgba(0,0,0,0.85)",backdropFilter:"blur(8px)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",padding:20},
  modal: {padding:28,maxWidth:600,width:"100%",maxHeight:"90vh",display:"flex",flexDirection:"column"},
  modalHead: {display:"flex",alignItems:"center",marginBottom:18,paddingBottom:14,borderBottom:"1px solid var(--au-hair)"},
  disclaimerBox: {background:"var(--au-glass)",border:"1px solid var(--au-border)",borderRadius:"var(--au-r-input)",padding:18,marginBottom:18,maxHeight:300,overflowY:"auto"},
};
